import React from 'react';
import { Switch, Tooltip, Card } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

import card1on from '../resources/card1on.png';
import card1off from '../resources/card1off.png';
import card2on from '../resources/card2on.png';
import card2off from '../resources/card2off.png';
import card3_1off_2off_3off from '../resources/card3-1off-2off-3off.png';
import card3_1off_2off_3on from '../resources/card3-1off-2off-3on.png';
import card3_1off_2on_3off from '../resources/card3-1off-2on-3off.png';
import card3_1off_2on_3on from '../resources/card3-1off-2on-3on.png';
import card3_1on_2off_3off from '../resources/card3-1on-2off-3off.png';
import card3_1on_2off_3on from '../resources/card3-1on-2off-3on.png';
import card3_1on_2on_3off from '../resources/card3-1on-2on-3off.png';
import card3_1on_2on_3on from '../resources/card3-1on-2on-3on.png';
import card4on from '../resources/card4on.png';
import card4off from '../resources/card4off.png';
import card5on from '../resources/card5on.png';
import card5off from '../resources/card5off.png';

interface VisualCustomizationTabProps {
    formik: any;
}

interface CustomizationCard {
    key: string;
    title: string;
    tooltip?: string;
    fieldName?: string;
    cardNumber: number;
    switches?: Array<{
        key: string;
        title: string;
        tooltip?: string;
        fieldName: string;
    }>;
}

const customizationCards: CustomizationCard[] = [
    {
        key: 'youWon',
        title: 'Final Winning Value',
        tooltip: 'Show the final winning value to players',
        fieldName: 'promotionSettings.youWonEnabled',
        cardNumber: 1,
    },
    {
        key: 'valuedAt',
        title: 'Value of Promotion',
        tooltip: 'Show the value of the promotion to players',
        fieldName: 'promotionSettings.valuedAtEnabled',
        cardNumber: 2,
    },
    {
        key: 'expiresIn',
        title: 'Campaign/Feature Player Expiry Time',
        tooltip: 'Show the expiry time for the campaign or feature',
        cardNumber: 3,
        switches: [
            {
                key: 'expiresIn',
                title: 'Campaign/Feature Player Expiry Time',
                tooltip: 'Show the expiry time for the campaign or feature',
                fieldName: 'promotionSettings.expiresInEnabled',
            },
            {
                key: 'playLater',
                title: 'Play Later Button',
                tooltip: 'Show the Play Later button',
                fieldName: 'promotionSettings.playLaterEnabled',
            },
            {
                key: 'optOut',
                title: 'Opt-Out Button and Function',
                tooltip: 'Enable the opt-out button and functionality',
                fieldName: 'promotionSettings.optOutEnabled',
            },
            {
                key: 'optOutConfirmation',
                title: 'Opt-Out Confirmation Popup',
                tooltip: 'Show a confirmation popup when players opt out',
                fieldName: 'promotionSettings.optOutConfirmationPopupEnabled',
            },
        ],
    },
];

const cardImages: Record<number, { on: string; off: string }> = {
    1: { on: card1on, off: card1off },
    2: { on: card2on, off: card2off },
    4: { on: card4on, off: card4off },
    5: { on: card5on, off: card5off },
};

const card3Images: Record<string, string> = {
    'off-off-off': card3_1off_2off_3off,
    'off-off-on': card3_1off_2off_3on,
    'off-on-off': card3_1off_2on_3off,
    'off-on-on': card3_1off_2on_3on,
    'on-off-off': card3_1on_2off_3off,
    'on-off-on': card3_1on_2off_3on,
    'on-on-off': card3_1on_2on_3off,
    'on-on-on': card3_1on_2on_3on,
};

const VisualCustomizationTab = ({ formik }: VisualCustomizationTabProps) => {
    const getFieldValue = (fieldName: string): boolean => {
        const keys = fieldName.split('.');
        let value = formik.values;
        for (const key of keys) {
            value = value?.[key];
        }
        return value ?? false;
    };

    const setFieldValue = (fieldName: string, value: boolean) => {
        const keys = fieldName.split('.');
        const currentSettings = formik.values.promotionSettings || {};
        const newSettings = { ...currentSettings };
        
        const lastKey = keys[keys.length - 1];
        newSettings[lastKey] = value;
        
        if (lastKey === 'optOutEnabled' && !value) {
            newSettings.optOutConfirmationPopupEnabled = false;
        }
        
        formik.setFieldValue('promotionSettings', newSettings);
    };

    const getCard3Image = (card: CustomizationCard): string => {
        if (!card.switches || card.cardNumber !== 3) {
            return '';
        }
        
        const switch1 = getFieldValue(card.switches[0].fieldName) ? 'on' : 'off';
        const switch2 = getFieldValue(card.switches[1].fieldName) ? 'on' : 'off';
        const switch3 = getFieldValue(card.switches[2].fieldName) ? 'on' : 'off';
        
        const key = `${switch1}-${switch2}-${switch3}`;
        return card3Images[key] || card3_1off_2off_3off;
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customizationCards.map((card) => {
                    const isEnabled = card.fieldName ? getFieldValue(card.fieldName) : false;
                    const imageSrc = card.cardNumber === 3 && card.switches 
                        ? getCard3Image(card)
                        : (isEnabled ? cardImages[card.cardNumber].on : cardImages[card.cardNumber].off);
                    
                    return (
                        <Card
                            key={card.key}
                            className="shadow-sm"
                            bodyStyle={{ padding: '12px' }}
                        >
                            <div className="flex flex-col h-full">
                                <div className="mb-3 bg-gray-100 rounded-lg overflow-hidden">
                                    <img
                                        src={imageSrc}
                                        alt={card.title}
                                        className="w-full h-auto object-contain"
                                    />
                                </div>
                                
                                {card.switches ? (
                                    <div className="pt-3 border-t border-gray-200 space-y-3">
                                        {card.switches.map((switchItem) => {
                                            const switchEnabled = getFieldValue(switchItem.fieldName);
                                            const isOptOutConfirmation = switchItem.fieldName === 'promotionSettings.optOutConfirmationPopupEnabled';
                                            const optOutEnabled = getFieldValue('promotionSettings.optOutEnabled');
                                            const isDisabled = isOptOutConfirmation && !optOutEnabled;
                                            
                                            return (
                                                <div key={switchItem.key} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={`text-sm font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                                                            {switchItem.title}
                                                        </span>
                                                        {switchItem.tooltip && (
                                                            <Tooltip title={switchItem.tooltip} placement="top">
                                                                <QuestionCircleOutlined className="text-gray-400 cursor-help text-xs" />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                    <Switch
                                                        checked={switchEnabled}
                                                        onChange={(checked) => setFieldValue(switchItem.fieldName, checked)}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-medium text-gray-900">
                                                {card.title}
                                            </span>
                                            {card.tooltip && (
                                                <Tooltip title={card.tooltip} placement="top">
                                                    <QuestionCircleOutlined className="text-gray-400 cursor-help text-xs" />
                                                </Tooltip>
                                            )}
                                        </div>
                                        <Switch
                                            checked={isEnabled}
                                            onChange={(checked) => card.fieldName && setFieldValue(card.fieldName, checked)}
                                        />
                                    </div>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default VisualCustomizationTab;

